import React, { useEffect, useRef, useState } from 'react';
import { CustomDropdown } from '../common/index.js';
import { Table, useTableState } from '../table/index.js';
import { DocumentsReadyForReviewChart, DocumentActivityChart, AssignedSummaryPieChart, DocumentCreationSourcePieChart, UserActivityOverTimeChart, UserActivityByStaffChart, DocumentProcessingTimesChart, formatSnapshotTimestamp } from './ChartComponents.js';
import { DocumentAssignmentsByAssigneeChart, ASSIGNMENT_CHART_CLICK_HINT } from './DocumentAssignmentsChart.js';
import { UserActionLabels } from './userActionEnum.js';
import MetricCard from './MetricCard.js';
import TableSectionHeader from './TableSectionHeader.js';
import { AggregationModeDropdown, ChartDownloadButton, ChartHeader, chartFileSlug } from './ChartCardControls.js';
import {
  buildAssigneeChartData,
  buildDocumentProcessingPoints,
  buildUserActivityOverTimeSeries,
} from './chartSeries.js';
import {
  downloadActivityOverTimeCSV,
  downloadAssigneeChartCSV,
  downloadDocumentProcessingCSV,
  downloadReadyForReviewCSV,
  downloadUserActivityOverTimeCSV,
} from './insightsCsvExport.js';

const pickColumns = (columns, keys) => keys
  .map((key) => columns.find((column) => column.key === key))
  .filter(Boolean);

const StaffWorkSection = ({
  title,
  subtitle,
  cards,
  dateRangeText,
  staffColumns,
  teamColumns,
  staffAggregateData,
  teamAggregateData,
  combinedStaffSelection,
  setSelectedStaffRows,
  selectedTeamRows,
  setSelectedTeamRows,
  staffTable,
  teamTable,
  onDownloadStaff,
  onDownloadTeam,
  staffDownloadDisabled,
  teamDownloadDisabled,
}) => (
  <section className="staff-work-section">
    <div className="staff-work-section-heading">
      <h2 className="staff-work-section-title">{title}</h2>
      <p className="staff-work-section-subtitle">{subtitle}</p>
    </div>
    <div className="insights-metrics">
      <div className="metrics-group">
        {cards.map((card) => (
          <MetricCard
            key={card.label}
            value={card.value}
            label={card.label}
            dateRange={dateRangeText}
          />
        ))}
      </div>
    </div>
    <div className="staff-table-container">
      <TableSectionHeader
        title="Staff"
        onDownload={onDownloadStaff}
        disabled={staffDownloadDisabled}
      />
      <Table
        data={staffAggregateData}
        columns={staffColumns}
        id={staffTable.id}
        enableSelection={true}
        selectedRows={combinedStaffSelection}
        onSelectionChange={setSelectedStaffRows}
        getRowKey={(row) => row.userId}
        emptyMessage="No staff data available for this time period"
        sortState={staffTable.sortState}
        onSortChange={staffTable.setSortState}
        filterValues={staffTable.filterValues}
        onFilterChange={staffTable.setFilterValues}
        disabledFilters={staffTable.disabledFilters}
        onDisabledFiltersChange={staffTable.setDisabledFilters}
        paginationState={staffTable.paginationState}
        onPaginationChange={staffTable.setPaginationState}
      />
    </div>
    <div className="staff-table-container">
      <TableSectionHeader
        title="Teams"
        onDownload={onDownloadTeam}
        disabled={teamDownloadDisabled}
      />
      <Table
        data={teamAggregateData}
        columns={teamColumns}
        id={teamTable.id}
        enableSelection={true}
        selectedRows={selectedTeamRows}
        onSelectionChange={setSelectedTeamRows}
        getRowKey={(row) => row.teamId}
        emptyMessage="No team data available for this time period"
        sortState={teamTable.sortState}
        onSortChange={teamTable.setSortState}
        filterValues={teamTable.filterValues}
        onFilterChange={teamTable.setFilterValues}
        disabledFilters={teamTable.disabledFilters}
        onDisabledFiltersChange={teamTable.setDisabledFilters}
        paginationState={teamTable.paginationState}
        onPaginationChange={teamTable.setPaginationState}
      />
    </div>
  </section>
);

const InsightsDocumentsSection = ({
  aggregationMode,
  averageTimeToFileAwayText,
  averageTimeToOpenText,
  averageTimeToReviewText,
  byStaffDropdownOpen,
  combinedMetrics,
  combinedStaffSelection,
  currentDocumentsAssigned,
  data,
  dateRangeText,
  documentsReadyDropdown,
  documentsReadyForReview,
  documentsReadyOptions,
  focusedAction,
  focusedActionDropdown,
  getActionTitle,
  getCurrentStateSnapshot,
  getExpandedUserIds,
  getUserSelectionText,
  handleDownloadStaffSpreadsheet,
  handleDownloadTeamSpreadsheet,
  handleFocusedActionChange,
  handleToggleAction,
  insightsType,
  onDocumentClick,
  processingTimeDropdown,
  processingTimeOptions,
  processingTimeSubtitles,
  processingTimeXAxisLabels,
  reviewData,
  selectedActions,
  selectedTeamRows,
  setByStaffDropdownOpen,
  setSelectedStaffRows,
  setSelectedTeamRows,
  setStaffTableDisabledFilters,
  setStaffTableFilterValues,
  setStaffTablePaginationState,
  setStaffTableSortState,
  setTeamTableDisabledFilters,
  setTeamTableFilterValues,
  setTeamTablePaginationState,
  setTeamTableSortState,
  showUserCharts,
  staffAggregateData,
  staffSpreadsheetDisabled,
  staffTableColumns,
  staffTableDisabledFilters,
  staffTableFilterValues,
  staffTablePaginationState,
  staffTableSortState,
  teamAggregateData,
  teamSpreadsheetDisabled,
  teamTableColumns,
  teamTableDisabledFilters,
  teamTableFilterValues,
  teamTablePaginationState,
  teamTableSortState,
  teams,
  totalDocumentIntake,
  userActionOptions,
  userIdToName,
  activeUsers = [],
  viewMode
}) => {
  const [staffWorkTab, setStaffWorkTab] = useState('overall');
  const staffWorkPanelRef = useRef(null);
  useEffect(() => {
    if (staffWorkPanelRef.current) {
      staffWorkPanelRef.current.scrollTop = 0;
    }
  }, [staffWorkTab]);
  const filingStaffTable = { id: 'staff-filing-insights-table', ...useTableState('StaffFilingInsightsTable') };
  const filingTeamTable = { id: 'team-filing-insights-table', ...useTableState('TeamFilingInsightsTable') };
  const assigningStaffTable = { id: 'staff-assigning-insights-table', ...useTableState('StaffAssigningInsightsTable') };
  const assigningTeamTable = { id: 'team-assigning-insights-table', ...useTableState('TeamAssigningInsightsTable') };
  const filteredUserIds = getExpandedUserIds();
  const assignedSummary = getCurrentStateSnapshot();
  const overTimeSeries = buildUserActivityOverTimeSeries({
    data,
    selectedAction: focusedAction,
    userIdToName,
    filteredUserIds,
    mode: aggregationMode.mode,
  });
  const assigneeSeries = buildAssigneeChartData(currentDocumentsAssigned, userIdToName, teams);
  const processingPoints = buildDocumentProcessingPoints(reviewData, processingTimeDropdown.selected.value);

  return (
  <>
            {insightsType === 'documents' && viewMode === 'gp-practice' && (
              <>
                <div className="insights-metrics">
                  <div className="metrics-group">
                    <MetricCard
                      value={totalDocumentIntake}
                      label="Total documents intake"
                      dateRange={dateRangeText}
                    />
                    <MetricCard
                      value={documentsReadyForReview}
                      label="Documents ready for review"
                      dateRange={formatSnapshotTimestamp(new Date().toISOString())}
                    />
                  </div>
                  <div className="metrics-group">
                    <MetricCard
                      value={averageTimeToOpenText}
                      label="Average time to first open document"
                      dateRange={dateRangeText}
                    />
                    <MetricCard
                      value={averageTimeToReviewText}
                      label="Average time from first opening to filing document"
                      dateRange={dateRangeText}
                    />
                    <MetricCard
                      value={averageTimeToFileAwayText}
                      label="Average time to review document"
                      dateRange={dateRangeText}
                    />
                  </div>
                </div>

                <div className="insights-container">
                  {/* Left Column */}
                  <div className="insights-column">
                    <div className="chart-card">
                      <ChartHeader title="Documents Ready for Review">
                        <CustomDropdown
                          options={documentsReadyOptions}
                          selectedOption={documentsReadyDropdown.selected.display}
                          onSelect={(option) => {
                            documentsReadyDropdown.handleSelect(option);
                          }}
                          isOpen={documentsReadyDropdown.isOpen}
                          toggleDropdown={() => documentsReadyDropdown.toggle([])}
                          keyExtractor={(option, index) => `documents-ready-${option.value || index}`}
                        />
                        <ChartDownloadButton
                          disabled={!data?.length}
                          onClick={() => downloadReadyForReviewCSV(
                            data,
                            documentsReadyDropdown.selected.value,
                            'documents_ready_for_review'
                          )}
                        />
                      </ChartHeader>
                      <div className="chart-subtitle-header">
                        Chart that illustrates how the backlog has changed in the selected time window
                      </div>
                      <div className="chart-content">
                        <DocumentsReadyForReviewChart data={data} type={documentsReadyDropdown.selected.value} />
                      </div>
                    </div>

                    {showUserCharts && (
                      <div className="chart-card">
                        <ChartHeader title={`${getActionTitle(focusedAction)} Over Time`}>
                          <AggregationModeDropdown
                            mode={aggregationMode.mode}
                            isOpen={aggregationMode.overTimeOpen}
                            onToggle={() => {
                              aggregationMode.toggleOverTime();
                              focusedActionDropdown.setIsOpen(false);
                            }}
                            onSelect={aggregationMode.select}
                          />
                          <CustomDropdown
                            options={userActionOptions}
                            selectedOption={UserActionLabels[focusedAction]}
                            onSelect={handleFocusedActionChange}
                            isOpen={focusedActionDropdown.isOpen}
                            toggleDropdown={() => {
                              focusedActionDropdown.toggle([]);
                              aggregationMode.close();
                            }}
                            keyExtractor={(option, index) => `user-activity-time-${option.value || index}`}
                          />
                          <ChartDownloadButton
                            disabled={overTimeSeries.userNames.length === 0}
                            onClick={() => downloadUserActivityOverTimeCSV({
                              data,
                              selectedAction: focusedAction,
                              userIdToName,
                              filteredUserIds,
                              mode: aggregationMode.mode,
                              filenamePrefix: `${chartFileSlug(getActionTitle(focusedAction))}_over_time`,
                            })}
                          />
                        </ChartHeader>
                        <div className="chart-subtitle-header">
                        Breakdown of selected activities for selected staff in selected time window, by staff
                        </div>
                        <div className="chart-content">
                          <UserActivityOverTimeChart 
                            data={data} 
                            selectedAction={focusedAction}
                            userIdToName={userIdToName}
                            filteredUserIds={filteredUserIds}
                            aggregationMode={aggregationMode.mode}
                            activeUsers={null}
                            onToggleUser={null}
                          />
                        </div>
                      </div>
                    )}

                    {showUserCharts && (
                      <div className="chart-card">
                        <ChartHeader title={`${getActionTitle(focusedAction)} by Staff`}>
                          <CustomDropdown
                            options={userActionOptions}
                            selectedOption={UserActionLabels[focusedAction]}
                            onSelect={handleFocusedActionChange}
                            isOpen={byStaffDropdownOpen}
                            toggleDropdown={() => setByStaffDropdownOpen(!byStaffDropdownOpen)}
                            keyExtractor={(option, index) => `user-activity-staff-${option.value || index}`}
                          />
                        </ChartHeader>
                        <div className="chart-subtitle-header">
                          Aggregate of '{getActionTitle(focusedAction).toLowerCase()}' activity by staff in selected time window
                        </div>
                        <div className="chart-content">
                          <UserActivityByStaffChart 
                            data={data} 
                            selectedAction={focusedAction}
                            userIdToName={userIdToName}
                            filteredUserIds={filteredUserIds}
                          />
                        </div>
                      </div>
                    )}
                    
                  </div>
                  
                  {/* Right Column */}
                  <div className="insights-column">
                    <div className="chart-card">
                      <ChartHeader title="Currently Assigned Documents">
                        <ChartDownloadButton
                          disabled={currentDocumentsAssigned == null || assigneeSeries.length === 0}
                          onClick={() => downloadAssigneeChartCSV(currentDocumentsAssigned, userIdToName, teams)}
                        />
                      </ChartHeader>
                      <div className="chart-subtitle-header">
                        Current assigned documents, grouped by user or team. {ASSIGNMENT_CHART_CLICK_HINT}
                      </div>
                      <div className="chart-content">
                        <DocumentAssignmentsByAssigneeChart
                          documentsAssigned={currentDocumentsAssigned}
                          userIdToName={userIdToName}
                          teams={teams}
                          activeUsers={activeUsers}
                        />
                      </div>
                    </div>

                    <div className="chart-card">
                      <ChartHeader title={`Document Activity (${getUserSelectionText()})`}>
                        <AggregationModeDropdown
                          mode={aggregationMode.mode}
                          isOpen={aggregationMode.activityOpen}
                          onToggle={aggregationMode.toggleActivity}
                          onSelect={aggregationMode.select}
                        />
                        <ChartDownloadButton
                          disabled={!data?.length || selectedActions.length === 0}
                          onClick={() => downloadActivityOverTimeCSV({
                            data,
                            actions: selectedActions,
                            userActionLabels: UserActionLabels,
                            filteredUserIds,
                            mode: aggregationMode.mode,
                            filenamePrefix: 'document_activity',
                          })}
                        />
                      </ChartHeader>
                      <div className="chart-subtitle-header">
                        Breakdown of selected activities for selected staff in selected time window, by activity type
                      </div>
                      <div className="chart-content">
                        <DocumentActivityChart
                          data={data}
                          allUserActions={selectedActions}
                          userActionLabels={UserActionLabels}
                          activeActions={selectedActions}
                          onToggleAction={handleToggleAction}
                          filteredUserIds={filteredUserIds}
                          aggregationMode={aggregationMode.mode}
                        />
                      </div>
                    </div>

                    <div className="chart-row">
                      {showUserCharts && (
                        <div className="chart-card chart-card-half">
                          <ChartHeader title="Document Intake Breakdown" />
                          <div className="chart-subtitle-header">
                            Source of documents arriving into MedPrompt in selected time window
                          </div>
                          <div className="chart-content">
                            <DocumentCreationSourcePieChart data={data} />
                          </div>
                        </div>
                      )}
                      
                      <div className="chart-card chart-card-half">
                        <ChartHeader title="Summary of Assigned Documents" />
                        <div className="chart-subtitle-header">
                          Whether documents are currently assigned or not
                        </div>
                        <div className="chart-content">
                          <AssignedSummaryPieChart 
                            data={assignedSummary} 
                            timestamp={new Date().toISOString()}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="chart-card">
                      <ChartHeader title="Document Processing Times">
                        <CustomDropdown
                          options={processingTimeOptions}
                          selectedOption={processingTimeDropdown.selected.display}
                          onSelect={(option) => {
                            processingTimeDropdown.handleSelect(option);
                          }}
                          isOpen={processingTimeDropdown.isOpen}
                          toggleDropdown={() => processingTimeDropdown.toggle([documentsReadyDropdown])}
                          keyExtractor={(option, index) => `processing-time-${option.value || index}`}
                        />
                        <ChartDownloadButton
                          disabled={processingPoints.length === 0}
                          onClick={() => downloadDocumentProcessingCSV(
                            reviewData,
                            processingTimeDropdown.selected.value,
                            processingTimeDropdown.selected.display,
                            processingTimeXAxisLabels[processingTimeDropdown.selected.value]
                          )}
                        />
                      </ChartHeader>
                      <div className="chart-subtitle-header">
                        {processingTimeSubtitles[processingTimeDropdown.selected.value]}
                      </div>
                      <div className="chart-content">
                        <DocumentProcessingTimesChart 
                          reviewData={reviewData} 
                          selectedMetric={processingTimeDropdown.selected.value}
                          onPointClick={onDocumentClick}
                          xAxisLabel={processingTimeXAxisLabels[processingTimeDropdown.selected.value]}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
      {viewMode === 'staff' && showUserCharts && insightsType === 'documents' && (
        <div className="staff-work-layout">
          <nav className="staff-work-tabs" aria-label="Staff work">
            {[
              ['overall', 'Overall'],
              ['filing', 'Filing'],
              ['assigning', 'Assigning'],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`staff-work-tab${staffWorkTab === id ? ' is-active' : ''}`}
                aria-current={staffWorkTab === id ? 'page' : undefined}
                onClick={() => setStaffWorkTab(id)}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className="staff-work-panel" ref={staffWorkPanelRef}>
          {staffWorkTab === 'overall' && (
          <StaffWorkSection
            title="Overall"
            subtitle="A summary of the documents they handled."
            dateRangeText={dateRangeText}
            cards={[
              { label: 'Documents worked', value: combinedMetrics.totalWorked },
              { label: 'Total time filing and assigning', value: combinedMetrics.totalWorkTime },
            ]}
            staffColumns={pickColumns(staffTableColumns, ['name', 'totalWorked', 'timeSpentFiling', 'timeSpentAssigning', 'totalWorkTime'])}
            teamColumns={pickColumns(teamTableColumns, ['teamName', 'memberCount', 'totalWorked', 'timeSpentFiling', 'timeSpentAssigning', 'totalWorkTime'])}
            staffAggregateData={staffAggregateData}
            teamAggregateData={teamAggregateData}
            combinedStaffSelection={combinedStaffSelection}
            setSelectedStaffRows={setSelectedStaffRows}
            selectedTeamRows={selectedTeamRows}
            setSelectedTeamRows={setSelectedTeamRows}
            staffTable={{
              id: 'staff-insights-table',
              sortState: staffTableSortState,
              setSortState: setStaffTableSortState,
              filterValues: staffTableFilterValues,
              setFilterValues: setStaffTableFilterValues,
              disabledFilters: staffTableDisabledFilters,
              setDisabledFilters: setStaffTableDisabledFilters,
              paginationState: staffTablePaginationState,
              setPaginationState: setStaffTablePaginationState,
            }}
            teamTable={{
              id: 'team-insights-table',
              sortState: teamTableSortState,
              setSortState: setTeamTableSortState,
              filterValues: teamTableFilterValues,
              setFilterValues: setTeamTableFilterValues,
              disabledFilters: teamTableDisabledFilters,
              setDisabledFilters: setTeamTableDisabledFilters,
              paginationState: teamTablePaginationState,
              setPaginationState: setTeamTablePaginationState,
            }}
            onDownloadStaff={handleDownloadStaffSpreadsheet}
            onDownloadTeam={handleDownloadTeamSpreadsheet}
            staffDownloadDisabled={staffSpreadsheetDisabled}
            teamDownloadDisabled={teamSpreadsheetDisabled}
          />
          )}
          {staffWorkTab === 'filing' && (
          <StaffWorkSection
            title="Filing"
            subtitle="Documents they filed away, and how long they spent on them."
            dateRangeText={dateRangeText}
            cards={[
              { label: 'Documents filed away', value: combinedMetrics.totalDocs },
              { label: 'Time reviewing documents', value: combinedMetrics.totalTime },
              { label: 'Average time to review a document', value: combinedMetrics.avgTime },
            ]}
            staffColumns={pickColumns(staffTableColumns, ['name', 'totalDocs', 'totalTime', 'avgTime'])}
            teamColumns={pickColumns(teamTableColumns, ['teamName', 'memberCount', 'totalDocs', 'totalTime', 'avgTime'])}
            staffAggregateData={staffAggregateData}
            teamAggregateData={teamAggregateData}
            combinedStaffSelection={combinedStaffSelection}
            setSelectedStaffRows={setSelectedStaffRows}
            selectedTeamRows={selectedTeamRows}
            setSelectedTeamRows={setSelectedTeamRows}
            staffTable={filingStaffTable}
            teamTable={filingTeamTable}
            onDownloadStaff={handleDownloadStaffSpreadsheet}
            onDownloadTeam={handleDownloadTeamSpreadsheet}
            staffDownloadDisabled={staffSpreadsheetDisabled}
            teamDownloadDisabled={teamSpreadsheetDisabled}
          />
          )}
          {staffWorkTab === 'assigning' && (
          <StaffWorkSection
            title="Assigning"
            subtitle="Documents they passed to someone else, and how long that took."
            dateRangeText={dateRangeText}
            cards={[
              { label: 'Documents assigned', value: combinedMetrics.totalAssigned },
              { label: 'Time assigning documents', value: combinedMetrics.assigningTime },
              { label: 'Average time to assign a document', value: combinedMetrics.avgAssigningTime },
            ]}
            staffColumns={pickColumns(staffTableColumns, ['name', 'totalAssigned', 'assigningTime', 'avgAssigningTime'])}
            teamColumns={pickColumns(teamTableColumns, ['teamName', 'memberCount', 'totalAssigned', 'assigningTime', 'avgAssigningTime'])}
            staffAggregateData={staffAggregateData}
            teamAggregateData={teamAggregateData}
            combinedStaffSelection={combinedStaffSelection}
            setSelectedStaffRows={setSelectedStaffRows}
            selectedTeamRows={selectedTeamRows}
            setSelectedTeamRows={setSelectedTeamRows}
            staffTable={assigningStaffTable}
            teamTable={assigningTeamTable}
            onDownloadStaff={handleDownloadStaffSpreadsheet}
            onDownloadTeam={handleDownloadTeamSpreadsheet}
            staffDownloadDisabled={staffSpreadsheetDisabled}
            teamDownloadDisabled={teamSpreadsheetDisabled}
          />
          )}
          </div>
        </div>
      )}
  </>
  );
};

export default InsightsDocumentsSection;
